import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Store, Shield } from "lucide-react";

interface DeviceAppsSettingsContentProps {
  deviceId?: string;
  isUsingAppleAppStore?: boolean;
  onAppStoreChange?: (useAppleStore: boolean) => Promise<void>;
  onAppFilterChange?: (appFilterValue: Record<string, boolean | { exclude?: { tags: string[]; type: "any" | "all" }; include?: { tags: string[]; type: "any" | "all" } }> | true) => Promise<void>;
}

export const DeviceAppsSettingsContent = ({
  deviceId,
  isUsingAppleAppStore = false,
  onAppStoreChange,
  onAppFilterChange
}: DeviceAppsSettingsContentProps) => {
  const [showAppleStoreWarning, setShowAppleStoreWarning] = useState(false);
  const [categoryWhitelistOption, setCategoryWhitelistOption] = useState<string>("categories");

  const handleAppStoreSelectionChange = (value: string) => {
    const useAppleStore = value === "apple";
    
    if (useAppleStore && !isUsingAppleAppStore) {
      setShowAppleStoreWarning(true);
    } else if (!useAppleStore && isUsingAppleAppStore) {
      onAppStoreChange?.(false);
    }
  };

  const confirmAppleStoreSwitch = () => {
    onAppStoreChange?.(true);
    setShowAppleStoreWarning(false);
  };

  return (
    <>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">App Settings</h2>
          <p className="text-gray-600 mt-1">Configure app store and installation preferences</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              App Store Selection
            </CardTitle>
            <CardDescription>
              Choose which app store this device should use for app installations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">
                  Current App Store
                </label>
                <Select
                  value={isUsingAppleAppStore ? "apple" : "cosr"}
                  onValueChange={handleAppStoreSelectionChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select app store" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cosr">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Kosher App Store</span>
                        <span className="text-xs text-gray-500">Filtered apps with parental controls</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="apple">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">Apple App Store</span>
                        <span className="text-xs text-gray-500">Full access to all iOS apps</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> {isUsingAppleAppStore 
                    ? "Apple App Store allows unrestricted app downloads. App filtering and restrictions are disabled."
                    : "Kosher App Store enforces app filtering based on categories, whitelists, and blacklists configured for this device's level."
                  }
                </p>
              </div>

              {/* Category and Whitelist Only Option - shown only for Kosher App Store */}
              {!isUsingAppleAppStore && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-2">
                    Select one option
                  </label>
                  <RadioGroup
                    value={categoryWhitelistOption}
                    onValueChange={async (value) => {
                      setCategoryWhitelistOption(value);
                      if (value === "whitelist" && !isUsingAppleAppStore) {
                        // Send empty object with special marker for whitelist-only mode
                        await onAppFilterChange?.({ "whitelist-only": true });
                      } else if (value === "categories" && !isUsingAppleAppStore) {
                        // Send appFilter: {} for categories mode (level defaults)
                        await onAppFilterChange?.({});
                      }
                    }}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="categories" id="categories" />
                      <Label htmlFor="categories" className="flex flex-col">
                        <span className="font-medium">Categories</span>
                        <span className="text-xs text-gray-500">Filter apps based on category rules</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="whitelist" id="whitelist" />
                      <Label htmlFor="whitelist" className="flex flex-col">
                        <span className="font-medium">Whitelist Only</span>
                        <span className="text-xs text-gray-500">Only allow specifically approved apps</span>
                      </Label>
                    </div>
                  </RadioGroup>
                  {!categoryWhitelistOption && (
                    <p className="text-red-600 text-xs mt-1">Please select one option to continue</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Apple App Store Warning Dialog */}
      <AlertDialog open={showAppleStoreWarning} onOpenChange={setShowAppleStoreWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to Apple App Store?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to do that? This will allow the user to download whatever app they want. 
              All current app restrictions and filters will be disabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowAppleStoreWarning(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAppleStoreSwitch}>
              Yes, Switch to Apple App Store
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};